<template>
  <div
    class="product-block-image"
    :class="[variant, { 'is-main': isMainProduct, 'preview-mode': isPreview }]"
  >
    <meta charset="UTF-8">
    <div class="product-content">
      <!-- Bouton de suppression -->
      <button v-if="!isPreview" class="remove-button" @click="handleRemove">
        <span class="text-red-600">×</span>
      </button>

      <!-- Product zone -->
      <div class="product-main" @dragover.prevent @drop.prevent="onProductDrop">
        <!-- Image container -->
        <div class="image-container" :style="{ background: backgroundColor }">
          <img
            v-if="product?.image"
            :src="product.image"
            :alt="product?.name"
            :class="[
              'product-image',
              { 'remove-background': removeBackground },
            ]"
          >
          <template v-else>
            <div v-if="!isPreview" class="empty-state">
              Déposez un produit ici
            </div>
            <div v-else class="empty-state-preview">
              <!-- Placeholder SVG pour le mode aperçu -->
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 100 100"
                class="default-product-svg"
                aria-hidden="true"
                focusable="false"
              >
                <rect width="100" height="100" fill="#f3f4f6" />
                <!-- Fond gris très clair -->
                <path
                  d="M85 20H15C12.24 20 10 22.24 10 25V75C10 77.76 12.24 80 15 80H85C87.76 80 90 77.76 90 75V25C90 22.24 87.76 20 85 20ZM80 70H20V30H80V70Z"
                  fill="#d1d5db"
                />
                <!-- Cadre image -->
                <circle
                  cx="35"
                  cy="45"
                  r="5"
                  fill="#d1d5db"
                />
                <!-- Petit cercle (soleil simplifié) -->
                <path d="M70 55L50 75L40 65L20 85H80L70 55Z" fill="#e5e7eb" />
              </svg>
            </div>
          </template>
          <!-- Toggle background removal -->
          <button
            v-if="product?.image && !isPreview"
            class="background-toggle"
            :title="
              removeBackground ? 'Restaurer le fond' : 'Supprimer le fond'
            "
            @click="toggleBackground"
          >
            <span class="icon">{{ removeBackground ? "🎨" : "✨" }}</span>
          </button>
          <!-- Background color toggle -->
          <button
            v-if="!isPreview"
            class="background-color-toggle"
            :title="'Changer la couleur du fond'"
            @click="toggleColorPicker"
          >
            <span class="icon">🎨</span>
          </button>
          <!-- Color picker -->
          <div
            v-if="showColorPicker && !isPreview"
            class="color-picker"
            @click.stop
          >
            <div class="color-options">
              <button
                v-for="color in predefinedColors"
                :key="color.value"
                class="color-option"
                :style="{ background: color.value }"
                :title="color.name"
                @click="selectBackgroundColor(color.value)"
              />
            </div>
            <input
              v-model="customColor"
              type="color"
              class="custom-color-input"
              @change="selectBackgroundColor(customColor)"
            >
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
export default {
  name: "ProductBlockImage",
};
</script>

<script setup lang="ts">
import { useProductCustomizationStore } from "@/stores/supports/useProductCustomizationStore";
import { computed, onMounted, ref } from "@vue/runtime-core";

interface Product {
  id: number;
  name: string;
  description: string;
  price: number;
  image: string;
  reference?: string;
  image_name?: string;
  product_image_id?: number;
}

interface ColorOption {
  name: string;
  value: string;
}

const props = defineProps<{
  product: Product | null;
  variant: "columnOne" | "rowOne" | "rowTwo" | "columnTwo";
  isMainProduct?: boolean;
  isPreview?: boolean;
}>();

const emit = defineEmits<{
  (e: "remove"): void;
  (e: "update:product", product: Product): void;
}>();

const customizationStore = useProductCustomizationStore();
const showColorPicker = ref<boolean>(false);
const customColor = ref<string>("#f8f8f8");

// Récupérer les données de personnalisation du store
const customization = computed(() => {
  if (!props.product?.id) return customizationStore.getDefaultCustomization();
  return customizationStore.getCustomization(props.product.id);
});

// Utiliser les données du store
const removeBackground = computed({
  get: () => customization.value.removeBackground || false,
  set: (value: boolean) => {
    if (props.product?.id) {
      customizationStore.setCustomization(props.product.id, {
        removeBackground: value,
      });
    }
  },
});

const backgroundColor = computed({
  get: () => customization.value.backgroundColor || "#f8f8f8",
  set: (value: string) => {
    if (props.product?.id) {
      customizationStore.setCustomization(props.product.id, {
        backgroundColor: value,
      });
    }
  },
});

const predefinedColors: ColorOption[] = [
  { name: "Gris clair", value: "#f8f8f8" },
  { name: "Blanc", value: "#ffffff" },
  { name: "Rouge AGCO", value: "#B70018" },
  { name: "Gris foncé", value: "#333333" },
  { name: "Bleu AGCO", value: "#003366" },
  { name: "Vert AGCO", value: "#006633" },
  { name: "Jaune AGCO", value: "#FFD700" },
];

const onProductDrop = (event: DragEvent): void => {
  if (!event.dataTransfer) return;

  try {
    const data = JSON.parse(event.dataTransfer.getData("text/plain"));
    if (data.type === "product" && data.data) {
      emit("update:product", {
        ...data.data,
        product_image_id: data.data.product_image_id,
      });
    }
  } catch (error) {
    console.error("Erreur lors du parsing des données:", error);
  }
};

const toggleBackground = () => {
  removeBackground.value = !removeBackground.value;
};

const toggleColorPicker = () => {
  showColorPicker.value = !showColorPicker.value;
};

const selectBackgroundColor = (color: string) => {
  backgroundColor.value = color;
  customColor.value = color;
  showColorPicker.value = false;
};

const resetProductData = () => {
  removeBackground.value = false;
  showColorPicker.value = false;
  backgroundColor.value = "#f8f8f8";
  customColor.value = "#f8f8f8";
};

const handleRemove = () => {
  resetProductData();
  emit("remove");
};

// Fermer le color picker quand on clique ailleurs
onMounted(() => {
  document.addEventListener("click", (event) => {
    if (showColorPicker.value) {
      const target = event.target as HTMLElement;
      if (
        !target.closest(".color-picker") &&
        !target.closest(".background-color-toggle")
      ) {
        showColorPicker.value = false;
      }
    }
  });
});

defineExpose({
  resetProductData,
});
</script>

<style scoped>
.product-block-image {
  position: relative;
  width: 100%;
  height: 100%;
  background: white;
  border-radius: 0.5rem;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.product-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 0.75rem;
  gap: 0.5rem;
  min-height: 0;
}

.product-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-height: 0;
  height: 100%;
}

.image-container {
  flex: 1;
  min-height: 0;
  width: 100%;
  background: #f8f8f8;
  border-radius: 0.5rem;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  isolation: isolate;
}

.product-image {
  width: 100%;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  background: transparent;
  transition: mix-blend-mode 0.3s ease;
}

.product-image.remove-background {
  mix-blend-mode: multiply;
  position: relative;
  z-index: 2;
  isolation: isolate;
}

.empty-state {
  color: #999;
  font-size: 0.875rem;
}

.empty-state-preview {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f8f8f8;
}

.default-product-svg {
  width: 60%;
  height: 60%;
  max-width: 100px;
  max-height: 100px;
}

.remove-button {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  width: 1.5rem;
  height: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 1.25rem;
  color: #b70018;
  z-index: 10;
}

.background-toggle {
  position: absolute;
  bottom: 1rem;
  left: 1rem;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #ccc;
  background: white;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 10;
}

.background-toggle:hover {
  border-color: #b70018;
  background: #f8f8f8;
}

.background-toggle .icon {
  font-size: 1.2rem;
}

.background-color-toggle {
  position: absolute;
  bottom: 1rem;
  left: 4rem;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #ccc;
  background: white;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 10;
}

.background-color-toggle:hover {
  border-color: #b70018;
  background: #f8f8f8;
}

.color-picker {
  position: absolute;
  bottom: 4rem;
  left: 4rem;
  background: white;
  border: 1px solid #ccc;
  border-radius: 0.5rem;
  padding: 0.5rem;
  z-index: 20;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.color-options {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.25rem;
  margin-bottom: 0.5rem;
}

.color-option {
  width: 2rem;
  height: 2rem;
  border: 1px solid #ccc;
  border-radius: 0.25rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.color-option:hover {
  transform: scale(1.1);
  border-color: #b70018;
}

.custom-color-input {
  width: 100%;
  height: 2rem;
  border: 1px solid #ccc;
  border-radius: 0.25rem;
  cursor: pointer;
  padding: 0;
}

/* Variants */
.columnOne .product-content,
.columnTwo .product-content {
  padding: 0.5rem;
}

.columnOne .image-container,
.columnTwo .image-container {
  flex: 1;
  min-height: 0;
  max-height: 60%;
}

.rowOne .product-content,
.rowTwo .product-content {
  flex-direction: row;
  gap: 0.75rem;
  align-items: center;
}

.rowOne .image-container,
.rowTwo .image-container {
  flex: 1;
  max-width: 50%;
  height: 100%;
  padding: 1rem;
}

.rowOne.is-main .product-content,
.rowTwo.is-main .product-content {
  padding: 1rem;
}

.rowOne.is-main .image-container,
.rowTwo.is-main .image-container {
  max-width: 100%;
  padding: 1.5rem;
}

/* Mode aperçu */
.preview-mode {
  border: none !important;
  background: transparent !important;
}

.preview-mode .product-content {
  padding: 0 !important;
}

.preview-mode .product-main {
  cursor: default !important;
}

.preview-mode .image-container {
  border: none !important;
  /* Préserver la couleur de fond personnalisée en mode aperçu */
}

.preview-mode .product-image {
  cursor: default !important;
}

.preview-mode .empty-state-preview {
  /* Préserver la couleur de fond personnalisée pour l'état vide aussi */
}

.preview-mode .default-product-svg {
  opacity: 0.5;
}

/* Masquer les contrôles en mode aperçu */
.preview-mode .remove-button,
.preview-mode .background-toggle,
.preview-mode .background-color-toggle,
.preview-mode .color-picker {
  display: none !important;
}

/* Rendre le contenu non éditable en mode aperçu */
.preview-mode [contenteditable="true"] {
  outline: none !important;
  cursor: default !important;
  user-select: none !important;
}

/* Supprimer les effets de survol en mode aperçu */
.preview-mode .product-block-image:hover,
.preview-mode .image-container:hover {
  border: none !important;
  /* Préserver la couleur de fond même au survol */
}
</style>
